import axios from "axios";
import { getApiUrl } from "@/Utils/api-url-helper";

/**
 * @typedef {Object} User
 * @property {number} id - El ID del usuario.
 * @property {string} name - El nombre del usuario.
 * @property {string} email - El correo electrónico del usuario.
 * @property {?string} imageUrl - La URL de la imagen del usuario (puede ser nula).
 * @property {?string} recoverToken - El token de recuperación (puede ser nulo).
 * @property {string} verifyToken - El token de verificación del correo electrónico.
 * @property {boolean} emailVerified - Indica si el correo electrónico está verificado.
 * @property {string} provider - El proveedor de autenticación.
 * @property {?string} providerId - El ID del proveedor (puede ser nulo).
 * @property {boolean} deleted - Indica si el usuario está eliminado.
 * @property {Array<Rol>} roles - Un array de roles asignados al usuario.
 */

/**
 * @typedef {Object} UserRol
 * @property {number} id - El ID del rol.
 * @property {string} name - El nombre del rol.
 */

/**
 * Login local con email y contraseña
 * @param {{username: string, password: string, email: string}} userInfo Correo y contraseña del usuario
 * @return {Promise<{accessToken: string, tokenType: string}>} Objeto con token del usuario
 */
export async function localLogin(userInfo) {
  const apiBaseUrl = getApiUrl(import.meta.env.VITE_API_BASE_URL);
  return axios.post(
    `${apiBaseUrl}/auth/login`,
    userInfo
  );
}

/**
 * Crea una ventana para iniciar sesión con Google
 * @param {function} callback Función a ejecutar cuando se realiza el login de Google
 */
export function googleLogin(callback) {
  const authWindow = window.open(
    `${import.meta.env.VITE_GOOGLE_AUTH_URL}&response_type=token&scope=email`,
    "googleAuthPopup",
    "width=500,height=600"
  );
  
  if (!authWindow) {
    console.error("[Google Login] No se pudo abrir la ventana popup. Puede estar bloqueada por el navegador.");
    return;
  }
  
  // Escuchar mensajes de la ventana popup
  const messageHandler = (event) => {
    if (
      event.origin === window.location.origin &&
      event.data && 
      event.data.type === "googleAuth"
    ) {
      // Remover el listener después de recibir el mensaje
      window.removeEventListener("message", messageHandler);
      
      if (event.data.token) {
        // Si recibimos un token, ejecutar el callback
        // El callback debería manejar el token y obtener los datos del usuario
        callback(event.data.token);
      } else if (event.data.error) {
        console.error("[Google Login] Error recibido:", event.data.error);
        // Manejar el error según sea necesario
        alert("Error en autenticación: " + event.data.error);
      }
      
      // Cerrar la ventana popup si aún está abierta
      try {
        if (authWindow && !authWindow.closed) {
          authWindow.close();
        }
      } catch (e) {
        console.warn("[Google Login] No se pudo cerrar la ventana popup:", e);
      }
    } else {
      console.log("[Google Login] Mensaje ignorado (origin o tipo no coincide)");
    }
  };
  
  window.addEventListener("message", messageHandler);
  
  // También escuchar cuando la ventana se cierra manualmente
  const checkClosed = setInterval(() => {
    try {
      if (authWindow.closed) {
        clearInterval(checkClosed);
        window.removeEventListener("message", messageHandler);
      }
    } catch (e) {
      // Ignorar errores de Cross-Origin-Opener-Policy
      console.warn("[Google Login] No se puede verificar si la ventana está cerrada (COOP):", e.message);
    }
  }, 500);
  
  // Limpiar el intervalo después de 5 minutos
  setTimeout(() => {
    clearInterval(checkClosed);
    window.removeEventListener("message", messageHandler);
  }, 5 * 60 * 1000);
}

/**
 * Obtiene la información del usuario usando el token del login
 * @param {string} token Bearer token de la respuesta de la autenticación
 * @return {Promise<UserRol>} Datos de la cuenta del usuario
 */
export async function getUserData(token) {
  const apiBaseUrl = getApiUrl(import.meta.env.VITE_API_BASE_URL);
  return axios.get(`${apiBaseUrl}/user/me`, {
    params: {},
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}
